import type { Cue, LoopLevels } from "./cues";

// The game's sound, all synthesized with Web Audio (nothing to download, so
// the one-file artifact stays one file): filtered noise for rolling and
// grinding, short noise bursts and falling sine thumps for pops, landings and
// bails, and little chimes for tricks, combos, gaps, goals and the menu. No
// music (the owner doesn't want any).
//
// Browsers only let a page make sound after a click or key press, so the
// AudioContext is made on the first one (`unlock`). Until then, and when
// Web Audio isn't there at all, everything here quietly does nothing.

const MASTER = 0.55;
/** Seconds the loop levels glide over, so speed changes don't zipper. */
const GLIDE = 0.05;

interface Loop {
  filter: BiquadFilterNode;
  gain: GainNode;
}

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private roll: Loop | null = null;
  private grind: Loop | null = null;
  private spray: Loop | null = null;
  private carve: Loop | null = null;
  private wobble: Loop | null = null;
  private wind: Loop | null = null;
  private _volume = 1;

  /** `volume` 0 (silent) .. 1 (full): the Settings slider (#36). */
  constructor(volume = 1) {
    this._volume = volume;
  }

  get volume(): number {
    return this._volume;
  }

  set volume(v: number) {
    this._volume = Math.max(0, Math.min(1, v));
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(MASTER * this._volume, this.ctx.currentTime, 0.02);
  }

  /** Volume all the way down: one-shots aren't even scheduled. */
  get muted(): boolean {
    return this._volume === 0;
  }

  /** Starts audio; call it from a click or key press. Safe to call again. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => {});
      return;
    }
    const Ctor = typeof window === "undefined" ? undefined : (window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = MASTER * this._volume;
      this.master.connect(ctx.destination);
      this.noise = noiseBuffer(ctx);
      // Concrete / wood rolling: a band of noise. Grinding: a narrow, resonant, scrapy band.
      this.roll = this.loop("bandpass", 700, 0.9);
      this.grind = this.loop("bandpass", 2500, 6);
      // The power washer (#151): a wide band of high noise, a hiss.
      this.spray = this.loop("bandpass", 4200, 0.5);
      // Bomb the Hill (#193): the wheels hissing through a carve, and the board's rattle in the speed wobbles.
      this.carve = this.loop("bandpass", 3000, 1.2);
      this.wobble = this.loop("bandpass", 240, 3);
      // On a hill (#194): the wind rushing past, a low wide band that rises with speed.
      this.wind = this.loop("bandpass", 450, 0.6);
    } catch {
      this.ctx = null;
    }
  }

  /** Sets the rolling, grinding and spraying loops (see `loopLevels`). */
  setLoops(l: LoopLevels): void {
    const ctx = this.ctx;
    if (!ctx || !this.roll || !this.grind || !this.spray) return;
    const t = ctx.currentTime;
    this.roll.gain.gain.setTargetAtTime(l.roll.gain * 0.22, t, GLIDE);
    this.roll.filter.frequency.setTargetAtTime(l.roll.freq, t, GLIDE);
    this.grind.gain.gain.setTargetAtTime(l.grind.gain * 0.3, t, GLIDE * 0.5);
    this.grind.filter.frequency.setTargetAtTime(l.grind.freq, t, GLIDE);
    this.spray.gain.gain.setTargetAtTime(l.spray.gain * 0.18, t, GLIDE * 0.5);
    this.spray.filter.frequency.setTargetAtTime(l.spray.freq, t, GLIDE);
    if (this.carve && this.wobble) {
      this.carve.gain.gain.setTargetAtTime(l.carve.gain * 0.2, t, GLIDE);
      this.carve.filter.frequency.setTargetAtTime(l.carve.freq, t, GLIDE);
      this.wobble.gain.gain.setTargetAtTime(l.wobble.gain * 0.35, t, GLIDE * 0.5);
      this.wobble.filter.frequency.setTargetAtTime(l.wobble.freq, t, GLIDE);
    }
    if (this.wind) {
      this.wind.gain.gain.setTargetAtTime(l.wind.gain * 0.3, t, GLIDE * 2);
      this.wind.filter.frequency.setTargetAtTime(l.wind.freq, t, GLIDE * 2);
    }
  }

  /** Plays a one-shot. `n` scales some of them (a trick's place in the combo raises its blip). */
  play(cue: Cue, n = 1): void {
    const ctx = this.ctx;
    if (!ctx || this.muted) return;
    const t = ctx.currentTime + 0.005;
    switch (cue) {
      case "pop":
        this.burst(t, 0.07, "highpass", 1800, 0.5);
        this.thump(t, 170, 70, 0.09, 0.6);
        break;
      case "land":
        this.thump(t, 110, 40, 0.16, 0.9);
        this.burst(t, 0.1, "lowpass", 1400, 0.45);
        break;
      case "sloppy":
        this.thump(t, 95, 35, 0.22, 1);
        this.burst(t, 0.18, "lowpass", 900, 0.6);
        break;
      case "perfect":
        this.thump(t, 110, 40, 0.14, 0.8);
        this.burst(t, 0.06, "lowpass", 1600, 0.35);
        this.tone(t + 0.02, 1568, 0.18, "triangle", 0.18);
        break;
      case "grind":
        this.burst(t, 0.08, "bandpass", 2600, 0.6);
        this.thump(t, 220, 120, 0.06, 0.35);
        break;
      case "bail":
        this.thump(t, 80, 28, 0.35, 1);
        this.burst(t, 0.35, "lowpass", 700, 0.7);
        break;
      case "trick": {
        const f = 660 * Math.pow(2, Math.min(12, n - 1) / 12);
        this.tone(t, f, 0.07, "square", 0.07);
        break;
      }
      case "combo":
        this.notes(t, [523, 659, 784], 0.07, "triangle", 0.16);
        break;
      case "gap":
        this.notes(t, [784, 1175], 0.08, "triangle", 0.18);
        break;
      case "goal":
        this.notes(t, [523, 659, 784, 1047], 0.09, "triangle", 0.2);
        break;
      case "fail":
        this.notes(t, [392, 330, 262], 0.12, "triangle", 0.16);
        break;
      case "pickup":
        this.notes(t, [1319, 1760], 0.05, "sine", 0.2);
        break;
      case "menu":
        this.tone(t, 880, 0.03, "square", 0.05);
        break;
      case "pick":
        this.notes(t, [880, 1320], 0.04, "square", 0.06);
        break;
      case "poof":
        this.thump(t, 700, 140, 0.12, 0.7);
        this.burst(t, 0.2, "highpass", 1200, 0.4);
        break;
      case "party":
        // A party horn: a buzzy blat that rises, then a second, longer toot.
        this.sweep(t, 330, 440, 0.16, "sawtooth", 0.12);
        this.sweep(t + 0.2, 350, 520, 0.4, "sawtooth", 0.12);
        this.burst(t, 0.12, "highpass", 3000, 0.25);
        break;
      case "whistle":
        // A rocket going up: a thin whistle that climbs, over a hiss.
        this.sweep(t, 900, 2400, 0.45, "sine", 0.07);
        this.burst(t, 0.4, "highpass", 4000, 0.12);
        break;
      case "bang":
        // A shell bursting, heard from a way off: a deep boom with a sharp edge.
        this.thump(t, 160, 38, 0.45, 0.9);
        this.burst(t, 0.3, "lowpass", 1200, 0.6);
        break;
      case "crackle":
        // The willow's stars popping: a scatter of tiny snaps.
        for (let i = 0; i < 7; i++) this.burst(t + i * 0.045 + (i % 3) * 0.012, 0.025, "highpass", 3500, 0.3);
        break;
      case "step":
        // A sneaker on concrete: a soft scuff and a low tap.
        this.burst(t, 0.05, "lowpass", 900, 0.25);
        this.thump(t, 140, 70, 0.05, 0.3);
        break;
      case "clack":
        // The board's tail on the ground: a hollow wooden knock.
        this.thump(t, 420, 180, 0.07, 0.6);
        this.burst(t, 0.05, "bandpass", 1900, 0.45);
        break;
      case "item":
        // Unzipping the pack and the item coming out: a quick rising zip and a click.
        this.burst(t, 0.09, "bandpass", 3200, 0.3);
        this.tone(t + 0.08, 1100, 0.03, "square", 0.05);
        break;
      case "shot":
        // A paintball marker's pneumatic pop: a short thud and a puff of air.
        this.thump(t, 300, 90, 0.06, 0.55);
        this.burst(t, 0.07, "highpass", 2200, 0.35);
        break;
      case "splat":
        // Paint hitting something: a wet slap that falls away.
        this.burst(t, 0.12, "lowpass", 1300, 0.45);
        this.thump(t, 180, 60, 0.08, 0.3);
        break;
      case "burst":
        // A target hit: a tinny clank and a bright little ding.
        this.thump(t, 520, 260, 0.06, 0.5);
        this.notes(t + 0.03, [1568, 2093], 0.06, "triangle", 0.14);
        break;
      case "clean":
        // A tag washed off: a squeaky-clean sparkle, three quick rising notes.
        this.notes(t, [1319, 1760, 2637], 0.07, "sine", 0.12);
        break;
      case "hey":
        // Someone on a crosswalk shouting "hey!" (#196): a voicy blat that jumps up and falls away.
        this.sweep(t, 260, 420, 0.08, "sawtooth", 0.1);
        this.sweep(t + 0.08, 420, 300, 0.16, "sawtooth", 0.1);
        this.burst(t, 0.05, "bandpass", 1800, 0.25);
        break;
      case "horn":
        // A side street's car honking (#197): two flat tones together, a long parp and a short one.
        for (const f of [392, 494]) {
          this.tone(t, f, 0.32, "square", 0.06);
          this.tone(t + 0.4, f, 0.16, "square", 0.06);
        }
        break;
      case "wobble":
        // Speed wobbles starting (#193): a quick two-note warning over a rattle.
        this.notes(t, [880, 660], 0.09, "square", 0.08);
        this.burst(t, 0.2, "bandpass", 300, 0.35);
        break;
      case "cheer":
        // A crowd going wild: a roar of voices (two bands of noise that swell
        // and die away), a couple of whistles and a scatter of claps.
        this.swell(t, 1.8, 900, 0.8, 0.5);
        this.swell(t + 0.05, 1.5, 2200, 1.2, 0.25);
        this.sweep(t + 0.15, 1400, 2300, 0.3, "sine", 0.06);
        this.sweep(t + 0.55, 1700, 2600, 0.25, "sine", 0.05);
        for (let i = 0; i < 12; i++) this.burst(t + 0.1 + i * 0.09 + Math.random() * 0.05, 0.03, "highpass", 2500, 0.18);
        break;
    }
  }

  dispose(): void {
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }

  private loop(type: BiquadFilterType, freq: number, q: number): Loop {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(filter).connect(gain).connect(this.master!);
    src.start();
    return { filter, gain };
  }

  /** A short filtered noise burst that dies away. */
  private burst(t: number, dur: number, type: BiquadFilterType, freq: number, vol: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    const gain = envelope(ctx, t, dur, vol);
    src.connect(filter).connect(gain).connect(this.master!);
    src.start(t, Math.random() * 0.5, dur + 0.05);
  }

  /** Filtered noise that builds for a moment, then dies away slowly over `dur`: a crowd's roar. */
  private swell(t: number, dur: number, freq: number, q: number, vol: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(this.master!);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.05);
  }

  /** A sine that drops in pitch: the body of a pop, a landing or a slam. */
  private thump(t: number, from: number, to: number, dur: number, vol: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(to, t + dur);
    osc.connect(envelope(ctx, t, dur, vol)).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private sweep(t: number, from: number, to: number, dur: number, type: OscillatorType, vol: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.linearRampToValueAtTime(to, t + dur);
    osc.connect(envelope(ctx, t, dur, vol)).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private tone(t: number, freq: number, dur: number, type: OscillatorType, vol: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(envelope(ctx, t, dur, vol)).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private notes(t: number, freqs: number[], each: number, type: OscillatorType, vol: number): void {
    freqs.forEach((f, i) => this.tone(t + i * each, f, each * 1.6, type, vol));
  }
}

/** A gain node that hits `vol` almost at once and decays to silence over `dur`. */
function envelope(ctx: AudioContext, t: number, dur: number, vol: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  return g;
}

/** One second of white noise, shared by every noisy sound. */
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}
